import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Box, Typography, Button } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8, 4),
    minHeight: '400px',
    textAlign: 'center'
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(3),
    animation: '$pulse 2s ease-in-out infinite'
  },
  icon: {
    fontSize: 60,
    color: theme.palette.text.secondary
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  message: {
    maxWidth: 500,
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  suggestions: {
    textAlign: 'left',
    maxWidth: 500,
    marginBottom: theme.spacing(4)
  },
  suggestionItem: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    '&::before': {
      content: '"• "',
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      marginRight: theme.spacing(1)
    }
  },
  resetButton: {
    textTransform: 'none',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1.5, 4),
    fontWeight: 600
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1
    },
    '50%': {
      transform: 'scale(1.05)',
      opacity: 0.8
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1
    }
  }
}));

const EmptyJobState = ({ hasFilters, onResetFilters }) => {
  const classes = useStyles();

  // Fun random messages for when filters return no results
  const funMessages = [
    {
      title: "Oops! Our jobs are playing hide and seek! 🙈",
      message: "These filters might be too picky. Let's widen the search and find your dream job!",
      emoji: "🔍"
    },
    {
      title: "No matches? That's just a plot twist! 🎬",
      message: "Every great career story has unexpected turns. Try adjusting your filters for the next chapter!",
      emoji: "📖"
    },
    {
      title: "404: Dream Job Not Found... Yet! 🚀",
      message: "But don't worry! Your perfect opportunity might be hiding in a different category. Let's explore!",
      emoji: "🗺️"
    },
    {
      title: "These filters are pickier than a food critic! 🍽️",
      message: "Sometimes the best opportunities come in unexpected packages. Cast a wider net!",
      emoji: "🎣"
    },
    {
      title: "Well, this is awkward... 😅",
      message: "No jobs match these filters. But hey, your perfect role might be just one click away!",
      emoji: "✨"
    }
  ];

  // Pick a random fun message
  const randomMessage = funMessages[Math.floor(Math.random() * funMessages.length)];

  return (
    <Box className={classes.container}>
      <Box className={classes.iconContainer}>
        <SearchIcon className={classes.icon} />
      </Box>

      <Typography variant="h4" className={classes.title}>
        {hasFilters ? randomMessage.title : "🌟 The Stage is Set... Awaiting the Stars! 🌟"}
      </Typography>

      <Typography variant="body1" className={classes.message}>
        {hasFilters
          ? randomMessage.message
          : "No job listings yet, but great things are coming! Companies post new opportunities daily. Bookmark us and check back soon - your dream job might be just around the corner! 🎯"}
      </Typography>

      {hasFilters && (
        <>
          <Box className={classes.suggestions}>
            <Typography variant="body2" className={classes.suggestionItem}>
              <span role="img" aria-label="art palette">🎨</span> Mix it up! Try different category combinations
            </Typography>
            <Typography variant="body2" className={classes.suggestionItem}>
              <span role="img" aria-label="globe">🌍</span> Go global! Remove filters to see ALL opportunities
            </Typography>
            <Typography variant="body2" className={classes.suggestionItem}>
              <span role="img" aria-label="clock">⏰</span> Timing is everything! New jobs drop daily
            </Typography>
            <Typography variant="body2" className={classes.suggestionItem}>
              <span role="img" aria-label="light bulb">💡</span> Pro tip: Sometimes the best jobs aren't in your "usual" category!
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.resetButton}
            startIcon={<RefreshIcon />}
            onClick={onResetFilters}>
            Show Me Everything! <span role="img" aria-label="party">🎉</span>
          </Button>
        </>
      )}

      {!hasFilters && (
        <Typography variant="body2" style={{ marginTop: 16, opacity: 0.7 }}>
          <span role="img" aria-label="briefcase">💼</span> Perfect jobs take time to find. We're working on bringing you the best opportunities!
        </Typography>
      )}
    </Box>
  );
};

EmptyJobState.propTypes = {
  hasFilters: PropTypes.bool,
  onResetFilters: PropTypes.func
};

EmptyJobState.defaultProps = {
  hasFilters: false,
  onResetFilters: () => {}
};

export default EmptyJobState;

